import { chatAiApi, mainApi } from '../index';
import type { Tag } from '@/features/email-flows/types/audience.types';
import type {
    EmailRecipientsListResponse,
    RecipientsListParams,
    RecipientsListResponse,
} from '@/features/email-flows/recipients/types';
import {
    AddressPayload,
    CreateFlowPayload,
    CreateFlowResponse,
    CreateTemplateFlowResponse,
    EmailFlowsResponse,
    FanTagsListResponse,
    FLOW_TYPE,
    FlowAnalyticsApiResponse,
    FlowsApiResponse,
    FlowSummary,
    PaginatedFlowsResponse,
    RecipientCountResponse,
    UpdateFlowPayload,
} from './types';
import type {
    GenerateBroadcastEmailRequest,
    GenerateBroadcastEmailResponse,
} from '@/features/email-flows/broadcasts/types/generation.types';

/**
 * Fetch all email flows (both event-triggered and user-triggered)
 * Normalizes the response to ensure consistent shape
 *
 * @param params - Optional pagination parameters
 * @param params.page - Page number for pagination
 * @param params.per_page - Number of items per page
 * @returns Promise resolving to flows API response containing all flows
 * @throws Error if the API request fails
 */
export async function fetchFlows(params?: { page?: number; per_page?: number }): Promise<FlowsApiResponse> {
    const { data } = await mainApi.get<FlowsApiResponse>('v1/flows', { params });
    return data;
}

/**
 * Fetch event-triggered email flows only with analytics
 * Filters and normalizes the response, then merges analytics data
 *
 * @param params - Optional pagination parameters
 * @param params.page - Page number for pagination
 * @param params.per_page - Number of items per page
 * @returns Promise resolving to object containing event-triggered flows with analytics
 * @throws Error if the API request fails
 */
export async function fetchEventTriggeredFlows(params?: {
    page?: number;
    per_page?: number;
}): Promise<EmailFlowsResponse> {
    const data = await fetchFlows(params);

    // Filter for event-triggered flows and sort by flow_id descending (newest first)
    const eventTriggered = data.flows
        .filter(flow => flow.type === FLOW_TYPE.EVENT_TRIGGERED)
        .sort((a, b) => b.flow_id - a.flow_id);

    // Fetch analytics and merge into flows
    try {
        const analyticsResponse = await fetchFlowAnalytics();
        const flowsWithAnalytics = mergeAnalyticsIntoFlows(eventTriggered, analyticsResponse.analytics);
        return { eventTriggered: flowsWithAnalytics };
    } catch (error) {
        // If analytics fetch fails, return flows without analytics
        console.error('Failed to fetch analytics:', error);
        return { eventTriggered };
    }
}

/**
 * Fetch flow analytics data
 * Fetches analytics for all flows with pagination support
 * Automatically fetches all pages until has_more is false
 *
 * @returns Promise resolving to flow analytics response
 * @throws Error if the API request fails
 */
export async function fetchFlowAnalytics(): Promise<FlowAnalyticsApiResponse> {
    let analytics: FlowAnalyticsApiResponse['analytics'] = {};
    let hasMore = true;
    let currentPage = 1;
    const pageLimit = 10;

    while (hasMore && currentPage <= pageLimit) {
        const { data } = await mainApi.get<FlowAnalyticsApiResponse>('v1/flows/analytics', {
            params: { page: currentPage },
        });

        analytics = { ...analytics, ...data.analytics };
        hasMore = data.has_more;
        currentPage += 1;

        if (currentPage > pageLimit) {
            console.error('Flow Analytics have fetched more than 10 pages - please check');
        }
    }

    return { analytics, has_more: false };
}

/**
 * Merge analytics data into flows
 * Adds analytics to both flow level and flow_emails level
 *
 * @param flows - Array of flows to merge analytics into
 * @param analyticsData - Analytics data from API
 * @returns Flows with merged analytics data
 */
function mergeAnalyticsIntoFlows(
    flows: FlowSummary[],
    analyticsData: FlowAnalyticsApiResponse['analytics']
): FlowSummary[] {
    return flows.map(flow => {
        const flowAnalytics = analyticsData[flow.flow_id];
        if (!flowAnalytics) return flow;

        // Add flow-level analytics
        const updatedFlow = {
            ...flow,
            analytics: flowAnalytics.analytics,
        };

        // Add email-level analytics
        if (updatedFlow.flow_emails) {
            updatedFlow.flow_emails = updatedFlow.flow_emails.map(email => ({
                ...email,
                analytics: flowAnalytics.emails[email.flow_email_id] || undefined,
            }));
        }

        return updatedFlow;
    });
}

/**
 * Fetch user-triggered flows (broadcasts) with analytics
 *
 * @param params - Optional pagination parameters
 * @param params.page - Page number for pagination
 * @param params.per_page - Number of items per page
 * @returns Promise resolving to array of broadcast flow summaries with analytics
 * @throws Error if the API request fails
 */
export async function fetchBroadcasts(params?: { page?: number; per_page?: number }): Promise<FlowSummary[]> {
    const data = await fetchFlows(params);

    // Filter for user-triggered flows and sort by flow_id descending (newest first)
    const broadcasts = data.flows
        .filter(flow => flow.type === FLOW_TYPE.USER_TRIGGERED)
        .sort((a, b) => b.flow_id - a.flow_id);

    // Fetch analytics and merge into broadcasts
    try {
        const analyticsResponse = await fetchFlowAnalytics();
        return mergeAnalyticsIntoFlows(broadcasts, analyticsResponse.analytics);
    } catch (error) {
        // If analytics fetch fails, return broadcasts without analytics
        console.error('Failed to fetch analytics:', error);
        return broadcasts;
    }
}

/**
 * Fetch user-triggered flows (broadcasts) with pagination
 * Uses backend type filtering and includes analytics in the same request
 *
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.per_page - Number of items per page
 * @returns Promise resolving to paginated broadcasts response with total count
 * @throws Error if the API request fails
 */
export async function fetchBroadcastsPaginated(params: {
    page: number;
    per_page: number;
}): Promise<PaginatedFlowsResponse> {
    const { data } = await mainApi.get<PaginatedFlowsResponse>('v1/flows', {
        params: {
            ...params,
            type: FLOW_TYPE.USER_TRIGGERED,
            include_analytics: true,
        },
    });
    return data;
}

/**
 * Fetch event-triggered flows with pagination
 * Uses backend type filtering and includes analytics in the same request
 *
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.per_page - Number of items per page
 * @returns Promise resolving to paginated flows response with total count
 * @throws Error if the API request fails
 */
export async function fetchEventTriggeredFlowsPaginated(params: {
    page: number;
    per_page: number;
}): Promise<PaginatedFlowsResponse> {
    const { data } = await mainApi.get<PaginatedFlowsResponse>('v1/flows', {
        params: {
            ...params,
            type: FLOW_TYPE.EVENT_TRIGGERED,
            include_analytics: true,
        },
    });
    return data;
}

/**
 * Update user's address for email flow onboarding
 *
 * @param address - Address payload containing street, city, state, postal_code, and country
 * @returns Promise that resolves when address is successfully updated
 * @throws Error if the API request fails
 */
export async function updateUserAddress(address: AddressPayload): Promise<void> {
    await mainApi.post('v1/users/address-update', address);
}

/**
 * Create a template flow for new users during onboarding
 *
 * @returns Promise resolving to the created template flow response
 * @throws Error if the API request fails
 */
export async function createTemplateFlow(): Promise<CreateTemplateFlowResponse> {
    const { data } = await mainApi.post<CreateTemplateFlowResponse>('v1/flows/create-template-flow');
    return data;
}

/**
 * Mark email flow onboarding as complete by setting a user tag
 *
 * @param userId - The ID of the user completing onboarding
 * @returns Promise that resolves when onboarding is marked complete
 * @throws Error if the API request fails
 */
export async function completeEmailFlowOnboarding(userId: number): Promise<void> {
    await mainApi.put('v1/tags', {
        name: 'email_flow_onboarding',
        type: 'user',
        reference_id: userId,
        data: { complete: 1 },
    });
}

/**
 * Delete a flow
 *
 * @param flowId - The ID of the flow to delete
 * @returns Promise that resolves when flow is successfully deleted
 * @throws Error if the API request fails
 */
export async function deleteFlow(flowId: number): Promise<void> {
    await mainApi.delete(`v1/flows/${flowId}`);
}

/**
 * Update flow status (publish/unpublish)
 *
 * @param flowId - The ID of the flow to update
 * @param status - The new status for the flow (e.g., 'published', 'draft')
 * @returns Promise that resolves when status is successfully updated
 * @throws Error if the API request fails
 */
export async function updateFlowStatus(flowId: number, status: string): Promise<void> {
    await mainApi.put('v1/flows/status', { flow_id: flowId, status });
}

/**
 * Duplicate a flow
 *
 * @param flowId - The ID of the flow to duplicate
 * @returns Promise resolving to the duplicated flow data
 * @throws Error if the API request fails
 */
export async function duplicateFlow(flowId: number): Promise<{ flow_id: number; type: number }> {
    const { data } = await mainApi.post<{ flow_id: number; type: number }>('v1/flows/duplicate', {
        flow_id: flowId,
    });
    return data;
}

/**
 * Update a flow's details (name, products, date/time, etc.)
 *
 * @param flowData - The flow data to update
 * @returns Promise that resolves when flow is successfully updated
 * @throws Error if the API request fails
 */
export async function updateFlow(flowData: UpdateFlowPayload): Promise<void> {
    await mainApi.put('v1/flows', flowData);
}

/**
 * Fetch recipient count based on selected tags
 *
 * @param tags - Combined tags array for filtering recipients
 * @returns Promise resolving to recipient count and fans data
 * @throws Error if the API request fails
 */
export async function fetchRecipientCount(tags: Tag[]): Promise<RecipientCountResponse> {
    const { data } = await mainApi.post<RecipientCountResponse>('v1/tags/tag-audience', { tags });
    return {
        count: data?.count || 0,
        fans: data?.fans || [],
    };
}

/**
 * Fetch available fan tags for audience selection
 *
 * @returns Promise resolving to array of tag names
 * @throws Error if the API request fails
 */
export async function fetchFanTags(): Promise<string[]> {
    const { data } = await mainApi.get<FanTagsListResponse>('/v1/tags/fan-tags-list');
    return data.tags || [];
}

/**
 * Create a new flow
 *
 * @param flowData - The flow data to create
 * @returns Promise resolving to the created flow ID
 * @throws Error if the API request fails
 */
export async function createFlow(flowData: CreateFlowPayload): Promise<CreateFlowResponse> {
    const { data } = await mainApi.put<CreateFlowResponse>('v1/flows', flowData);
    return data;
}

/**
 * Fetch a specific flow by ID
 *
 * @param flowId - The ID of the flow to fetch
 * @returns Promise resolving to the flow data
 * @throws Error if the API request fails
 */
export async function fetchFlow(flowId: number): Promise<FlowSummary> {
    const { data } = await mainApi.get<FlowSummary>(`v1/flows/${flowId}`);
    return data;
}

/**
 * Send a broadcast immediately
 *
 * @param broadcastData - The broadcast data including flow_id, flow_email_id, and tags
 * @returns Promise that resolves when broadcast is sent
 * @throws Error if the API request fails
 */
export async function sendBroadcast(broadcastData: {
    flow_id: number;
    flow_email_id: number;
    tags: Tag[];
}): Promise<void> {
    await mainApi.post('v1/flows/broadcast', broadcastData);
}

/**
 * Preview broadcast recipient count
 *
 * @param previewData - The preview data including optional flow_id, flow_email_id, and tags
 * @returns Promise resolving to recipient count
 * @throws Error if the API request fails
 */
export async function previewBroadcast(previewData: {
    flow_id?: number;
    flow_email_id?: number;
    tags: Tag[];
}): Promise<{ count: number }> {
    const { data } = await mainApi.post<{ count: number }>('v1/flows/preview-broadcast', previewData);
    return data;
}

/**
 * Fetch recipients list with pagination and filters
 *
 * @param params - Query parameters including page, pageSize, and filter_array
 * @returns Promise resolving to recipients list and total pages
 * @throws Error if the API request fails
 */
export async function fetchRecipientsList(params: RecipientsListParams): Promise<RecipientsListResponse> {
    const { data } = await mainApi.get<RecipientsListResponse>('v1/flows/recipient-list', { params });
    return data;
}

/**
 * Fetch recipients list for a specific email with pagination and filters
 * This shows who received a particular email in a flow
 *
 * @param emailId - The flow_email_id to fetch recipients for
 * @param params - Query parameters including page, pageSize, and filter_array
 * @returns Promise resolving to recipients list and total pages
 * @throws Error if the API request fails
 */
export async function fetchEmailRecipientsList(
    emailId: number,
    params: Omit<RecipientsListParams, 'flow_email_id'>
): Promise<EmailRecipientsListResponse> {
    const { data } = await mainApi.get<EmailRecipientsListResponse>(`v1/flows/recipient-list/${emailId}`, {
        params,
    });
    return data;
}

/**
 * Fetch opt-in email template variables and defaults
 * Uses the same endpoint as Vue (/v1/pages/email-templates) to get all email templates
 *
 * @returns Promise resolving to opt-in email template data
 * @throws Error if the API request fails
 */
export async function fetchOptInEmailTemplate(): Promise<{
    emailTemplates: {
        opt_in_email_template: {
            default: { subject: string; body: string };
            variables: {
                subject: Array<{ name: string; id: string; required: boolean }>;
                body: Array<{ name: string; id: string; required: boolean }>;
            };
        };
    };
}> {
    const { data } = await mainApi.get('v1/pages/email-templates');
    return data;
}

/**
 * Send opt-in emails to recipients who require opt-in
 *
 * @param payload - Email template data including subject and body
 * @returns Promise resolving to number of emails triggered
 * @throws Error if the API request fails
 */
export async function sendOptInEmails(payload: {
    subject_template: string;
    body_template: string;
}): Promise<{ emails_triggered: number }> {
    const { data } = await mainApi.post<{ emails_triggered: number }>('v1/flows/send-opt-in-emails', payload);
    return data;
}

/**
 * Generate broadcast email content using Stanley AI
 *
 * @param request - The generation request containing prompt and user info
 * @returns Promise resolving to the generated email content
 * @throws Error if the API request fails
 */
export async function generateBroadcastEmail(
    request: GenerateBroadcastEmailRequest
): Promise<GenerateBroadcastEmailResponse> {
    const { data } = await chatAiApi.post<GenerateBroadcastEmailResponse>('/v2/chat-ai/write-broadcast', request);
    return data;
}
